
import PropTypes from 'prop-types'
import React from 'react'
import moment from 'moment'
import clone from 'clone'
import Table from './Table2'
import { getStatusLabelReact as getStatusLabel } from 'lib/utils'
import numeral from 'numeral'
import prettyMs from 'pretty-ms'
import WarningIcon from './icons/warning'
import './RunsTable.css'

/**
 * Columns.
 */

const COLUMNS = [
  { label: 'Status', name: 'status', type: 'boolean', width: '110px' },
  { label: 'Started', name: 'when', type: 'date', header: true },
  { label: 'Duration', name: 'duration', type: 'string', width: '150px' },
  { label: 'Synced', name: 'synced', type: 'number', width: '150px' },
  { label: 'Notices', name: 'notice', type: 'string', width: '165px' }
]

const RunsTable = props => {
  let { runs, onClick } = props
  let rows = runs.map(transform).map(toRow)

  return (
    <div className='RunsTable'>
      <Table
        emptyText='There are no runs for this data source'
        columns={COLUMNS}
        rows={rows}
        sidePadding='20px'
        keyField='id' />
    </div>
  )

  // Transform the given `run` to have a more descriptive overview
  // per the Sources' team request (https://paper.dropbox.com/doc/Source-Run-States-V7TUM7CDOV7HpbApLnKFw)
  // TODO: move to api or source report generator
  function transform (run) {
    const updatedRun = clone(run)
    const runCollections = run.collections || []
    const didAllCollectionsSucceed = runCollections.reduce((curr, collection) => {
      return curr && collection.status === 'success'
    }, true) && runCollections.length > 0

    if (run.status === 'error') {
      updatedRun.notice = 'Check run details'

      // there was an error and no objects synced
      if (runCollections.length > 0 && run.synced === 0) {
        // there was an error but all of the collections succeeded
        if (didAllCollectionsSucceed) {
          updatedRun.status = 'success-warning'
          updatedRun.notice = 'Partial run with errors'
        } else {
          // there was an error and at least one of the collections failed
          updatedRun.status = 'empty'
        }
      } else {
        if (run.synced === 0) {
          // there was a global source error and no data went through
          updatedRun.status = 'error'
        } else {
          // there was a global source error and some data went through
          updatedRun.status = 'success-warning'
          updatedRun.notice = 'Partial run with errors'
        }
      }
    }

    // successful sync but no objects synced
    if (run.status === 'success' && run.synced === 0) {
      updatedRun.status = 'success-empty'
      updatedRun.notice = 'No data found'
    }

    return updatedRun
  }

  // Transform the given `run` to a `row`.
  function toRow (run) {
    const syncTime = typeof run.synced === 'number' ? numeral(run.synced).format('0,0') : ''
    // if the sync is still pending, it should not be clickable
    const onClickLogic = run.status !== 'pending'
      ? onClick({
        id: run.id,
        run_id: run.run_id,
        status: run.status
      })
      : null

    const createdAt = moment(run.createdAt)

    return {
      id: {
        value: run.id,
        content: null
      },
      status: {
        value: run.status,
        content: getStatusLabel(run.status)
      },
      when: {
        value: run.createdAt,
        content: createdAt.fromNow(),
        title: createdAt.format('LLLL')
      },
      duration: {
        value: run.duration,
        content: prettyMs(run.duration * 1000)
      },
      synced: {
        value: run.synced,
        content: syncTime
      },
      notice: {
        value: run.notice,
        content: run.notice ? (
          <span>
            <WarningIcon
              width={14}
              height={14}
              margin='5px 0 0'
              display='inline-block' />
            <span style={{ verticalAlign: 'top' }}> {run.notice}</span>
          </span>
        ) : <span />
      },
      onClick: onClickLogic
    }
  }
}

RunsTable.displayName = 'RunsTable'

RunsTable.propTypes = {
  runs: PropTypes.array.isRequired,
  onClick: PropTypes.func.isRequired
}

export default RunsTable



// WEBPACK FOOTER //
// ./ui/RunsTable.js